import type { Group, GroupId } from "@repo/app-types/entities/group";
import { asGroupId } from "@repo/app-types/entities/group";
import { Size } from "@repo/ui/helpers/helpers";
import {
  MultiSelect,
  type MultiSelectOption,
  type MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import { useEffect, useEffectEvent, useMemo, useState, type FC } from "react";

export interface TeammateGroupFilterItemProps {
  allTeammateGroupsLabel: string;
  groups: readonly Group[];
  // `null` represents "All teammates" — i.e. no teammate-group filter
  // applied. An array (possibly empty) represents an explicit multi-select.
  value: readonly GroupId[] | null;
  onChange: (newValue: readonly GroupId[] | null) => void;
  isDisabled?: boolean;
}

// Sentinel id used to represent the always-present "All teammates" entry in
// the dropdown. Picking it clears the filter (`null`); selecting any group
// drops the sentinel.
const ALL_TEAMMATES_OPTION_ID = "__all_teammates__";

// Debounce window for propagating selection changes to `props.onChange`,
// so rapid toggling of options inside the popover doesn't fire an
// `onChange` (and therefore a refetch / preference write) for every
// click.
const ON_CHANGE_DEBOUNCE_MS = 500;

export const TeammateGroupFilterItem: FC<TeammateGroupFilterItemProps> = (
  props,
) => {
  // Local draft of the selection that the popover renders against. We
  // debounce propagating it back through `props.onChange`, but the
  // dropdown itself needs immediate feedback so the user sees the boxes
  // tick on click.
  const [draftValue, setDraftValue] = useState<readonly GroupId[] | null>(
    props.value,
  );

  // A selection that hasn't reached `props.onChange` yet. Each change starts
  // the debounce window over; wrapped so that re-picking the same selection
  // still restarts it.
  const [pendingChange, setPendingChange] = useState<{
    value: readonly GroupId[] | null;
  } | null>(null);

  const emitChange = useEffectEvent((value: readonly GroupId[] | null) => {
    props.onChange(value);
  });

  // When the parent pushes a new value (e.g. preferences load, the
  // filter is reset, or another component changes it) override the
  // draft and drop any pending emit so we don't immediately
  // overwrite the new value with stale draft state.
  useEffect(() => {
    setDraftValue(props.value);
    setPendingChange(null);
  }, [props.value]);

  useEffect(() => {
    if (!pendingChange) {
      return;
    }

    const timeout = setTimeout(() => {
      setPendingChange(null);
      emitChange(pendingChange.value);
    }, ON_CHANGE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [pendingChange]);

  // Flush pending changes on unmount so the parent doesn't lose a
  // selection that was still inside the debounce window.
  const flushPendingChange = useEffectEvent(() => {
    if (pendingChange) {
      props.onChange(pendingChange.value);
    }
  });
  useEffect(() => {
    return () => {
      flushPendingChange();
    };
  }, []);

  const knownGroupIds = useMemo(
    () => new Set(props.groups.map((g) => g.id)),
    [props.groups],
  );

  // Surface previously-saved group ids that no longer resolve (e.g. a group
  // was deleted) as "Unknown group" entries so the user can still see and
  // remove the stale selection.
  const unknownIds = useMemo(
    () => (draftValue ?? []).filter((id) => !knownGroupIds.has(id)),
    [draftValue, knownGroupIds],
  );

  // Flag the dropdown as invalid when every selected id is unknown — the
  // filter is effectively pointing at nothing.
  const allSelectedAreUnknown =
    draftValue !== null &&
    draftValue.length > 0 &&
    draftValue.every((id) => !knownGroupIds.has(id));

  const sections = useMemo<readonly MultiSelectSection[]>(() => {
    const groupOptions: MultiSelectOption[] = [
      {
        id: ALL_TEAMMATES_OPTION_ID,
        label: props.allTeammateGroupsLabel,
      },
      ...props.groups.map((g) => ({ id: g.id, label: g.name })),
      ...unknownIds.map((id) => ({ id, label: "Unknown group" })),
    ];
    return [{ id: "teammate-groups", options: groupOptions }];
  }, [props.allTeammateGroupsLabel, props.groups, unknownIds]);

  // Selected ids fed to MultiSelect. When the filter is `null` ("All
  // teammates"), surface the synthetic entry as the sole selection so the
  // dropdown reflects "no filter".
  const selectedIds = useMemo<readonly string[]>(
    () => (draftValue === null ? [ALL_TEAMMATES_OPTION_ID] : [...draftValue]),
    [draftValue],
  );

  const handleChange = (nextSelection: readonly string[]) => {
    const prevIsAll = draftValue === null;
    const nextIsAll = nextSelection.includes(ALL_TEAMMATES_OPTION_ID);
    const nextSelectedIds = nextSelection.filter(
      (id) => id !== ALL_TEAMMATES_OPTION_ID,
    );

    const nextDraft: readonly GroupId[] | null =
      (!prevIsAll && nextIsAll) || nextSelectedIds.length === 0
        ? null
        : nextSelectedIds.map((id) => asGroupId(id));
    setDraftValue(nextDraft);
    setPendingChange({ value: nextDraft });
  };

  const placeholder = useMemo(() => {
    if (draftValue === null) {
      return props.allTeammateGroupsLabel;
    }
    if (draftValue.length === 1) {
      const [onlyId] = draftValue;
      const group = props.groups.find((g) => g.id === onlyId);
      return group?.name ?? "Unknown group";
    }
    return `${draftValue.length} groups`;
  }, [draftValue, props.allTeammateGroupsLabel, props.groups]);

  return (
    <MultiSelect
      label={placeholder}
      placeholder={placeholder}
      sections={sections}
      value={selectedIds}
      onChange={handleChange}
      isDisabled={props.isDisabled}
      isInvalid={allSelectedAreUnknown}
      size={Size.SMALL}
    />
  );
};
